import React, { useEffect, useState } from 'react';
import { scrollToTargetCenter } from '../../../../../utils/scroll.ts';
import { RestaurantDetails_getRestaurantDetails_menuCard } from '../../../../../types/graphql-types-generated.ts';
import { Icon } from '../../../../../patterns/00-atoms/icon/icon.tsx';
import { Toggletip } from '../../../../../patterns/00-atoms/toggletip/toggletip.tsx';
import { classList } from '../../../../../utils/string.ts';
import { safeWindow } from '../../../../../utils/window.ts';
import { UBTracking } from '../../../../../components/tracking/ubtracking.ts';

export type MenuCategoriesProps = {
  categories: RestaurantDetails_getRestaurantDetails_menuCard['categories'];
  selectedCategoryId: number | undefined;
  setSelectedCategoryId: (id: number | undefined) => void;
};

export const MenuCategories: React.FunctionComponent<MenuCategoriesProps> = ({
  categories,
  selectedCategoryId,
  setSelectedCategoryId
}) => {
  const horizontalMovementDistance = 300;
  const [displayArrowLeft, setDisplayArrowLeft] = useState<boolean>(false);
  const [displayArrowRight, setDisplayArrowRight] = useState<boolean>(false);
  const categoriesContainerRef = React.useRef<HTMLDivElement>(null);

  if (categoriesContainerRef.current) {
    let isScrolling: number | NodeJS.Timeout | undefined;

    categoriesContainerRef.current.addEventListener('scroll', () => {
      safeWindow()?.clearTimeout(isScrolling);

      isScrolling = setTimeout(() => {
        displayArrowHandler();
      }, 150);
    });
  }

  const displayArrowHandler = () => {
    const categoriesContainer = categoriesContainerRef.current;

    if (!categoriesContainer) {
      return;
    }

    const categoriesContainerChildren = categoriesContainer.children;
    const secondChild = categoriesContainerChildren[1];
    const lastChild = categoriesContainerChildren[categoriesContainerChildren.length - 1];

    const offsetWidth = categoriesContainer.offsetWidth;
    const scrollWidth = categoriesContainer.scrollWidth;
    const scrollLimit = scrollWidth - offsetWidth;
    let scrollLeft = Math.round(categoriesContainer.scrollLeft);
    let horizontalMovementDistance = 0;

    horizontalMovementDistance = (secondChild && secondChild.clientWidth / 2) || 0;
    if (scrollLimit >= horizontalMovementDistance) {
      if (scrollLeft < horizontalMovementDistance && scrollLeft !== 0) {
        categoriesContainer.scrollLeft = 0;
        scrollLeft = 0;
      }
    }

    horizontalMovementDistance = (lastChild && lastChild.clientWidth / 2) || 0;
    if (scrollLimit >= horizontalMovementDistance) {
      if (scrollLimit - scrollLeft < horizontalMovementDistance && scrollLeft !== scrollLimit) {
        categoriesContainer.scrollLeft = scrollLimit;
        scrollLeft = scrollLimit;
      }
    }

    setDisplayArrowRight(scrollLeft < scrollLimit);
    setDisplayArrowLeft(scrollLeft > 0);
  };

  const handleScrollLeft = () => {
    const categoriesContainer = categoriesContainerRef.current;

    if (!categoriesContainer) {
      return;
    }

    categoriesContainer.scrollLeft -= horizontalMovementDistance;

    displayArrowHandler();
  };

  const handleScrollRight = () => {
    const categoriesContainer = categoriesContainerRef.current;

    if (!categoriesContainer) {
      return;
    }

    categoriesContainer.scrollLeft += horizontalMovementDistance;

    displayArrowHandler();
  };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const categoryId = parseInt(hash.replace('#kategorie-', ''), 10);
      if (!isNaN(categoryId)) {
        setSelectedCategoryId(categoryId);
      }
    }

    displayArrowHandler();

    if (categoriesContainerRef.current) {
      UBTracking.onTrackTargetView(
        categoriesContainerRef.current,
        'sticky_menu_categories',
        'category_container'
      );
    }
  }, [categories]);

  useEffect(() => {
    if (selectedCategoryId !== undefined && selectedCategoryId !== null) {
      const target = document.querySelector(`a[href="#kategorie-${selectedCategoryId}"]`);
      const parent = categoriesContainerRef.current;
      if (target && parent) {
        scrollToTargetCenter(target as HTMLElement, parent);
      }
    }
  }, [selectedCategoryId]);

  if (!categories) {
    return null;
  }

  return (
    <div
      className="bg-sk-grey-0 sticky top-12 isolate z-10 w-full max-w-full max-sm:hidden"
      id="menu-categories"
    >
      <div
        ref={categoriesContainerRef}
        className="scrollbar-none flex flex-row items-center gap-4 overflow-x-auto scroll-smooth px-4 whitespace-nowrap [&::-webkit-scrollbar]:h-0"
      >
        <Toggletip
          button={props => (
            <button
              className="bg-sk-grey-0 border-sk-grey-200 sticky -left-4 z-30 -mr-4 -ml-4 cursor-pointer border-b-4 text-2xl"
              {...props}
            >
              <Icon name="menu-1" className="block p-4" />
            </button>
          )}
          content={
            <div className="flex flex-col gap-4">
              {categories.map(category => {
                const checked = category?.id === selectedCategoryId;

                return (
                  <a
                    href={`#kategorie-${category?.id}`}
                    key={category?.id}
                    className={classList(
                      'relative z-10 flex items-center justify-between gap-4',
                      checked ? 'font-bold' : 'font-medium'
                    )}
                    onClick={() => {
                      UBTracking.state.category = category;
                      UBTracking.trackEvent.click(
                        'sticky_menu_categories',
                        'category_item',
                        'list'
                      );
                      setSelectedCategoryId(category?.id);
                    }}
                  >
                    <span className="text-sm leading-6">{category?.name}</span>
                    <Icon name="check" className={classList('text-2xl', !checked && 'opacity-0')} />
                  </a>
                );
              })}
            </div>
          }
          onOpen={() => UBTracking.trackEvent.click('sticky_menu_categories', 'list', 'open')}
          onClose={() => UBTracking.trackEvent.click('sticky_menu_categories', 'list', 'close')}
          paddingClass="p-4"
          modal={true}
        />
        {categories.map(category => (
          <a
            href={`#kategorie-${category?.id}`}
            key={category?.id}
            className="relative"
            onClick={() => {
              UBTracking.state.category = category;
              UBTracking.trackEvent.click('sticky_menu_categories', 'category_item', 'bar');
            }}
          >
            <span className="text-sm font-bold">{category?.name}</span>
            <div
              className={classList(
                'bg-sk-blue-500 absolute top-9 z-20 h-1 w-full',
                category?.id !== selectedCategoryId && 'hidden'
              )}
            ></div>
          </a>
        ))}
      </div>
      {displayArrowLeft && (
        <div className="from-sk-grey-0 to-sk-grey-0/50 absolute top-0 left-14 w-24 bg-linear-to-r">
          <button className="bg-sk-grey-0/50 z-10 block p-4 text-2xl" onClick={handleScrollLeft}>
            <Icon name="arrow-left" />
          </button>
        </div>
      )}
      {displayArrowRight && (
        <div className="from-sk-grey-0 to-sk-grey-0/50 absolute top-0 right-0 flex w-24 justify-end bg-linear-to-l">
          <button className="bg-sk-grey-0/50 z-10 block p-4 text-2xl" onClick={handleScrollRight}>
            <Icon name="arrow-right" />
          </button>
        </div>
      )}
      <div className="bg-sk-grey-200 absolute bottom-0 z-10 h-1 w-full"></div>
    </div>
  );
};
